/* assets/rocup-calc-core.js
   Shared core for RocUp calculators (pace + treadmill)
   - No dependencies
   - Shopify-safe
*/
(function () {
  "use strict";

  /* ---------------------------
     Utilities
  --------------------------- */
  function $(root, sel) { return root.querySelector(sel); }
  function $all(root, sel) { return Array.from(root.querySelectorAll(sel)); }

  function kmToMi(km) { return km * 0.621371192; }
  function miToKm(mi) { return mi / 0.621371192; }

  function clamp(n, min, max) {
    const x = Number(n);
    if (!Number.isFinite(x)) return min;
    return Math.min(max, Math.max(min, x));
  }

  function pad2(n) { return String(n).padStart(2, "0"); }

  function formatSecondsToHMS(sec) {
    if (!Number.isFinite(sec) || sec < 0) return "–";
    const s = Math.round(sec);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    if (h > 0) return `${pad2(h)}:${pad2(m)}:${pad2(r)}`;
    return `${pad2(m)}:${pad2(r)}`;
  }

  function formatSecondsToPace(sec) {
    if (!Number.isFinite(sec) || sec <= 0) return "–";
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return `${pad2(m)}:${pad2(s)}`;
  }

  function fillSelectRange(sel, start, end) {
    if (!sel) return;
    sel.innerHTML = "";
    for (let i = start; i <= end; i++) {
      const opt = document.createElement("option");
      opt.value = String(i);
      opt.textContent = pad2(i);
      sel.appendChild(opt);
    }
  }

  function secondsFromHMS(h, m, s) {
    return (Number(h) || 0) * 3600 + (Number(m) || 0) * 60 + (Number(s) || 0);
  }
  function secondsFromMS(m, s) {
    return (Number(m) || 0) * 60 + (Number(s) || 0);
  }

  function collectQuery(paramsObj) {
    const p = new URLSearchParams();
    Object.entries(paramsObj || {}).forEach(([k, v]) => {
      if (v === null || v === undefined) return;
      const str = String(v);
      if (str === "") return;
      p.set(k, str);
    });
    return p;
  }

  function safeNumber(n, fallback) {
    const x = Number(n);
    return Number.isFinite(x) ? x : fallback;
  }

  /* ---------------------------
     Shared: Duration select fill
     (fixes "dropdown empty" issues)
  --------------------------- */
  function fillDurationSelects(root, defaults) {
    const hAll = root.querySelectorAll('select[data-dur="h"]');
    const mAll = root.querySelectorAll('select[data-dur="m"]');
    const sAll = root.querySelectorAll('select[data-dur="s"]');

    function fillAll(nodeList, start, end) {
      Array.from(nodeList).forEach(sel => fillSelectRange(sel, start, end));
    }

    fillAll(hAll, 0, 9);
    fillAll(mAll, 0, 59);
    fillAll(sAll, 0, 59);

    const defH = (defaults && defaults.h != null) ? String(defaults.h) : "0";
    const defM = (defaults && defaults.m != null) ? String(defaults.m) : "30";
    const defS = (defaults && defaults.s != null) ? String(defaults.s) : "0";

    Array.from(hAll).forEach(s => { s.value = defH; });
    Array.from(mAll).forEach(s => { s.value = defM; });
    Array.from(sAll).forEach(s => { s.value = defS; });
  }

  /* ---------------------------
     Shared: Login modal helpers
  --------------------------- */
  function mountLoginModal(root, cfg) {
    const modal = $(root, "[data-login-modal]");
    const closeBtn = $(root, "[data-login-close]");
    const loginLink = $(root, "[data-login-link]");
    const registerLink = $(root, "[data-register-link]");

    if (!modal) {
      return { show: function () {}, hide: function () {}, refreshLinks: function () {} };
    }

    function buildAuthHref(base) {
      const stateParams = (typeof cfg.stateCollectorFn === "function")
        ? cfg.stateCollectorFn()
        : new URLSearchParams();

      const returnWithQuery = `${cfg.returnPath}?${stateParams.toString()}`;
      return `${base}?return_url=${encodeURIComponent(returnWithQuery)}`;
    }

    function refreshLinks() {
      if (!loginLink || !registerLink) return;
      loginLink.href = buildAuthHref(cfg.loginBase);
      registerLink.href = buildAuthHref(cfg.registerBase);
    }

    function show() {
      refreshLinks();
      modal.classList.remove("is-hidden");
      document.documentElement.style.overflow = "hidden";
    }

    function hide() {
      modal.classList.add("is-hidden");
      document.documentElement.style.overflow = "";
    }

    closeBtn && closeBtn.addEventListener("click", hide);
    modal.addEventListener("click", (e) => { if (e.target === modal) hide(); });

    return { show, hide, refreshLinks };
  }

  /* =========================================================
     Treadmill Calculator Mount
  ========================================================= */
  function mountTreadmillCalculator(root, userCfg) {
    const cfg = Object.assign({
      sectionId: "",
      isLoggedIn: false,
      loginBase: "/account/login",
      registerBase: "/account/register",
      returnPath: "/pages/treadmill-pace-calculator",
      defaults: { mode: "speed", units: "metric" }
    }, userCfg || {});

    // Core elements
    const unitToggle = $(root, "#unitToggle");
    const modeBtns = $all(root, ".mode-btn");
    const speedMode = $(root, "#speedMode");
    const paceMode = $(root, "#paceMode");

    // Inputs
    const speedWhole = $(root, "#speedWhole");
    const speedFrac  = $(root, "#speedFrac");
    const paceMin    = $(root, "#paceMin");
    const paceSec    = $(root, "#paceSec");

    // Duration (BOTH forms) via data attributes
    const durHAll = $all(root, 'select[data-dur="h"]');
    const durMAll = $all(root, 'select[data-dur="m"]');
    const durSAll = $all(root, 'select[data-dur="s"]');

    // Outputs
    const outSpeed = $(root, "#outSpeed");
    const outPace  = $(root, "#outPace");
    const outDist  = $(root, "#outDist");
    const outTime  = $(root, "#outTime");

    // Buttons
    const applySpeed = $(root, "#applySpeed");
    const applyPace  = $(root, "#applyPace");

    // Labels
    const speedUnitLabel = $all(root, "[data-speed-unit]");
    const paceUnitLabel  = $all(root, "[data-pace-unit]");

    function isImperial() { return !!(unitToggle && unitToggle.checked); }

    function updateUnitLabels() {
      const speedUnit = isImperial() ? "mph" : "km/h";
      const paceUnit  = isImperial() ? "/mi" : "/km";
      speedUnitLabel.forEach(el => el.textContent = speedUnit);
      paceUnitLabel.forEach(el => el.textContent = paceUnit);
    }

    function setMode(mode) {
      const speedActive = mode === "speed";
      if (speedMode) speedMode.classList.toggle("is-hidden", !speedActive);
      if (paceMode)  paceMode.classList.toggle("is-hidden", speedActive);
      modeBtns.forEach(btn => btn.setAttribute("aria-selected", String(btn.dataset.mode === mode)));
    }

    // Duration sync helpers
    function getDurH() { return durHAll[0] ? durHAll[0].value : "0"; }
    function getDurM() { return durMAll[0] ? durMAll[0].value : "0"; }
    function getDurS() { return durSAll[0] ? durSAll[0].value : "0"; }

    function setDuration(h, m, s) {
      durHAll.forEach(el => { el.value = String(h); });
      durMAll.forEach(el => { el.value = String(m); });
      durSAll.forEach(el => { el.value = String(s); });
    }

    function getDurationSeconds() {
      const t = secondsFromHMS(getDurH(), getDurM(), getDurS());
      return Number.isFinite(t) && t > 0 ? t : 0;
    }

    function getSpeedInCurrentUnits() {
      const whole = safeNumber(speedWhole && speedWhole.value, 0);
      const frac  = safeNumber(speedFrac && speedFrac.value, 0);
      const sp = whole + (frac / 10);
      return Number.isFinite(sp) && sp > 0 ? sp : 0;
    }

    function speedToPaceSeconds(speed) {
      if (!Number.isFinite(speed) || speed <= 0) return NaN;
      return 3600 / speed;
    }

    function paceSecondsToSpeed(paceSecVal) {
      if (!Number.isFinite(paceSecVal) || paceSecVal <= 0) return NaN;
      return 3600 / paceSecVal;
    }

    function distanceFromSpeedAndTime(speed, timeSec) {
      if (!Number.isFinite(speed) || speed <= 0) return NaN;
      if (!Number.isFinite(timeSec) || timeSec <= 0) return NaN;
      return speed * (timeSec / 3600);
    }

    function writeOutputs(speedVal, paceSecVal, distVal, timeSecVal) {
      const speedUnit = isImperial() ? "mph" : "km/h";
      const paceUnit  = isImperial() ? "/mi" : "/km";
      const distUnit  = isImperial() ? "mi"  : "km";

      if (outSpeed) outSpeed.textContent = (Number.isFinite(speedVal) && speedVal > 0) ? `${speedVal.toFixed(1)} ${speedUnit}` : "–";
      if (outPace)  outPace.textContent  = (Number.isFinite(paceSecVal) && paceSecVal > 0) ? `${formatSecondsToPace(paceSecVal)} ${paceUnit}` : "–";
      if (outDist)  outDist.textContent  = (Number.isFinite(distVal) && distVal > 0) ? `${distVal.toFixed(2).replace(/\.00$/, "")} ${distUnit}` : "–";
      if (outTime)  outTime.textContent  = (Number.isFinite(timeSecVal) && timeSecVal > 0) ? formatSecondsToHMS(timeSecVal) : "–";
    }

    function calculateFromSpeed() {
      if (!cfg.isLoggedIn) return;
      const speed = getSpeedInCurrentUnits();
      const timeSec = getDurationSeconds();
      const paceSecVal = speedToPaceSeconds(speed);
      const distVal = distanceFromSpeedAndTime(speed, timeSec);
      writeOutputs(speed, paceSecVal, distVal, timeSec);
    }

    function calculateFromPace() {
      if (!cfg.isLoggedIn) return;
      const paceSecVal = secondsFromMS(paceMin && paceMin.value, paceSec && paceSec.value);
      const timeSec = getDurationSeconds();
      if (!Number.isFinite(paceSecVal) || paceSecVal <= 0) {
        writeOutputs(NaN, NaN, NaN, timeSec);
        return;
      }
      const speed = paceSecondsToSpeed(paceSecVal);
      const distVal = distanceFromSpeedAndTime(speed, timeSec);
      writeOutputs(speed, paceSecVal, distVal, timeSec);
    }

    function activeMode() {
      return (root.querySelector('.mode-btn[aria-selected="true"]')?.dataset.mode) || cfg.defaults.mode;
    }

    /* ---------- URL state ---------- */
    function collectStateParams() {
      const dur = getDurationSeconds();
      const paceSecVal = secondsFromMS(paceMin && paceMin.value, paceSec && paceSec.value);
      return collectQuery({
        u: isImperial() ? "i" : "m",
        mode: activeMode(),
        sw: speedWhole ? speedWhole.value : "",
        sf: speedFrac ? speedFrac.value : "",
        pm: (paceSecVal > 0) ? String(safeNumber(paceMin && paceMin.value, 0)) : "",
        ps: (paceSecVal > 0) ? String(safeNumber(paceSec && paceSec.value, 0)) : "",
        dh: (dur > 0) ? String(safeNumber(getDurH(), 0)) : "",
        dm: (dur > 0) ? String(safeNumber(getDurM(), 0)) : "",
        ds: (dur > 0) ? String(safeNumber(getDurS(), 0)) : ""
      });
    }

    function parsePrefillFromURL() {
      const q = new URLSearchParams(window.location.search || "");
      if (![...q.keys()].length) return null;
      return {
        units: q.get("u") === "i" ? "imperial" : (q.get("u") === "m" ? "metric" : null),
        mode: q.get("mode"),
        sw: q.get("sw"),
        sf: q.get("sf"),
        pm: q.get("pm"),
        ps: q.get("ps"),
        dh: q.get("dh"),
        dm: q.get("dm"),
        ds: q.get("ds")
      };
    }

    function applyPrefill(pref) {
      if (!pref) return;

      if (pref.units && unitToggle) {
        unitToggle.checked = (pref.units === "imperial");
        updateUnitLabels();
      }
      if (pref.mode === "speed" || pref.mode === "pace") setMode(pref.mode);

      if (speedWhole && pref.sw != null) speedWhole.value = String(clamp(pref.sw, 0, 40));
      if (speedFrac  && pref.sf != null) speedFrac.value  = String(clamp(pref.sf, 0, 9));

      if (paceMin && pref.pm != null) paceMin.value = String(clamp(pref.pm, 0, 30));
      if (paceSec && pref.ps != null) paceSec.value = String(clamp(pref.ps, 0, 59));

      const dh = (pref.dh != null) ? String(clamp(pref.dh, 0, 9))  : null;
      const dm = (pref.dm != null) ? String(clamp(pref.dm, 0, 59)) : null;
      const ds = (pref.ds != null) ? String(clamp(pref.ds, 0, 59)) : null;
      if (dh !== null || dm !== null || ds !== null) {
        setDuration(dh ?? getDurH(), dm ?? getDurM(), ds ?? getDurS());
      }

      if (cfg.isLoggedIn) {
        if (activeMode() === "speed") calculateFromSpeed();
        else calculateFromPace();
      }
    }

    /* ---------- Modal ---------- */
    const modal = mountLoginModal(root, {
      isLoggedIn: cfg.isLoggedIn,
      loginBase: cfg.loginBase,
      registerBase: cfg.registerBase,
      returnPath: cfg.returnPath,
      stateCollectorFn: collectStateParams
    });

    /* ---------- Events ---------- */
    unitToggle && unitToggle.addEventListener("change", () => {
      updateUnitLabels();
      if (!cfg.isLoggedIn) return;
      if (activeMode() === "speed") calculateFromSpeed();
      else calculateFromPace();
    });

    modeBtns.forEach(btn => {
      btn.addEventListener("click", () => {
        setMode(btn.dataset.mode);
        if (!cfg.isLoggedIn) return;
        if (activeMode() === "speed") calculateFromSpeed();
        else calculateFromPace();
      });
    });

    applySpeed && applySpeed.addEventListener("click", () => {
      if (!cfg.isLoggedIn) { modal.show(); return; }
      calculateFromSpeed();
    });

    applyPace && applyPace.addEventListener("click", () => {
      if (!cfg.isLoggedIn) { modal.show(); return; }
      calculateFromPace();
    });

    // Duration dropdown sync + recalc
    function bindDurationSync(selects, which) {
      selects.forEach(sel => sel.addEventListener("change", () => {
        const h = which === "h" ? sel.value : getDurH();
        const m = which === "m" ? sel.value : getDurM();
        const s = which === "s" ? sel.value : getDurS();
        setDuration(h, m, s);

        if (!cfg.isLoggedIn) return;
        if (activeMode() === "speed") calculateFromSpeed();
        else calculateFromPace();
      }));
    }

    bindDurationSync(durHAll, "h");
    bindDurationSync(durMAll, "m");
    bindDurationSync(durSAll, "s");

    [speedWhole, speedFrac].forEach(el => el && el.addEventListener("change", () => {
      if (!cfg.isLoggedIn) return;
      if (activeMode() === "speed") calculateFromSpeed();
    }));

    [paceMin, paceSec].forEach(el => el && el.addEventListener("change", () => {
      if (!cfg.isLoggedIn) return;
      if (activeMode() === "pace") calculateFromPace();
    }));

    /* ---------- Init selects ---------- */
    fillSelectRange(speedWhole, 0, 40);
    fillSelectRange(speedFrac, 0, 9);
    fillSelectRange(paceMin, 0, 30);
    fillSelectRange(paceSec, 0, 59);

    // Duration selects in BOTH forms (speed + pace)
    fillDurationSelects(root, { h: 0, m: 30, s: 0 });

    // Defaults
    if (speedWhole) speedWhole.value = "10";
    if (speedFrac)  speedFrac.value  = "0";
    if (paceMin)    paceMin.value    = "5";
    if (paceSec)    paceSec.value    = "0";

    if (unitToggle) unitToggle.checked = (cfg.defaults.units === "imperial");
    updateUnitLabels();
    setMode(cfg.defaults.mode);

    applyPrefill(parsePrefillFromURL());
    writeOutputs(NaN, NaN, NaN, getDurationSeconds());

    return { calculateFromSpeed, calculateFromPace };
  }

  /* =========================================================
     Pace Calculator Mount
     (populates dropdowns + basic calculations)
  ========================================================= */
  function mountPaceCalculator(root, userCfg) {
    const cfg = Object.assign({
      sectionId: "",
      isLoggedIn: false,
      loginBase: "/account/login",
      registerBase: "/account/register",
      returnPath: "/pages/pace-calculator",
      defaults: { mode: "pace", units: "metric", showSplits: true },
      distances: []
    }, userCfg || {});

    const unitToggle = $(root, "#unitToggle");
    const modeBtns = $all(root, ".mode-btn");
    const paceMode = $(root, "#paceMode");
    const goalMode = $(root, "#goalMode");

    const paceMin = $(root, "#paceMin");
    const paceSec = $(root, "#paceSec");
    const applyPace = $(root, "#applyPace");

    const goalDistance = $(root, "#goalDistance");
    const customDistance = $(root, "#customDistance");
    const customOnly = $all(root, ".custom-only");
    const goalH = $(root, "#goalH");
    const goalM = $(root, "#goalM");
    const goalS = $(root, "#goalS");
    const applyGoal = $(root, "#applyGoal");

    const unitLabelInline = $(root, ".unit-label");
    const unitDistanceSuffix = $(root, ".unit-distance");

    const paceRequiredCard = $(root, "#paceRequired");
    const reqPerKm = $(root, "#reqPerKm");
    const reqPerMi = $(root, "#reqPerMi");

    const splitsWrap = $(root, "#splitsWrap");
    const splitsBody = $(root, "#splitsBody");
    const splitStep = $(root, "#splitStep");

    const cardsGrid = $(root, "#cardsGrid");

    function isImperial() { return !!(unitToggle && unitToggle.checked); }

    function setMode(mode) {
      const isPace = mode === "pace";
      if (paceMode) paceMode.classList.toggle("is-hidden", !isPace);
      if (goalMode) goalMode.classList.toggle("is-hidden", isPace);
      modeBtns.forEach(btn => btn.setAttribute("aria-selected", String(btn.dataset.mode === mode)));
      updateUnitLabels();
    }

    function activeMode() {
      return (root.querySelector('.mode-btn[aria-selected="true"]')?.dataset.mode) || cfg.defaults.mode;
    }

    function updateUnitLabels() {
      const per = isImperial() ? "per mile" : "per km";
      if (unitLabelInline) unitLabelInline.textContent = per;
      if (unitDistanceSuffix) unitDistanceSuffix.textContent = isImperial() ? "mi" : "km";
    }

    function collectStateParams() {
      const pm = safeNumber(paceMin && paceMin.value, 0);
      const ps = safeNumber(paceSec && paceSec.value, 0);
      const gh = safeNumber(goalH && goalH.value, 0);
      const gm = safeNumber(goalM && goalM.value, 0);
      const gs = safeNumber(goalS && goalS.value, 0);
      const distKey = goalDistance ? goalDistance.value : "";
      const custom = customDistance ? customDistance.value : "";

      return collectQuery({
        u: isImperial() ? "i" : "m",
        mode: activeMode(),
        pm: String(pm),
        ps: String(ps),
        d: distKey,
        cd: custom,
        gh: String(gh),
        gm: String(gm),
        gs: String(gs),
        step: splitStep ? splitStep.value : ""
      });
    }

    function parsePrefillFromURL() {
      const q = new URLSearchParams(window.location.search || "");
      if (![...q.keys()].length) return null;
      return {
        units: q.get("u") === "i" ? "imperial" : (q.get("u") === "m" ? "metric" : null),
        mode: q.get("mode"),
        pm: q.get("pm"),
        ps: q.get("ps"),
        d: q.get("d"),
        cd: q.get("cd"),
        gh: q.get("gh"),
        gm: q.get("gm"),
        gs: q.get("gs"),
        step: q.get("step")
      };
    }

    function applyPrefill(pref) {
      if (!pref) return;

      if (pref.units && unitToggle) unitToggle.checked = (pref.units === "imperial");
      updateUnitLabels();

      if (pref.mode === "pace" || pref.mode === "goal") setMode(pref.mode);

      if (paceMin && pref.pm != null) paceMin.value = String(clamp(pref.pm, 0, 30));
      if (paceSec && pref.ps != null) paceSec.value = String(clamp(pref.ps, 0, 59));

      if (goalDistance && pref.d != null && pref.d !== "") goalDistance.value = pref.d;
      if (customDistance && pref.cd != null && pref.cd !== "") customDistance.value = pref.cd;

      if (goalH && pref.gh != null) goalH.value = String(clamp(pref.gh, 0, 9));
      if (goalM && pref.gm != null) goalM.value = String(clamp(pref.gm, 0, 59));
      if (goalS && pref.gs != null) goalS.value = String(clamp(pref.gs, 0, 59));

      if (splitStep && pref.step) splitStep.value = pref.step;

      refreshCustomVisibility();
      if (cfg.isLoggedIn) {
        if (activeMode() === "pace") calculateFromPace();
        else calculateFromGoal();
      }
    }

    const modal = mountLoginModal(root, {
      isLoggedIn: cfg.isLoggedIn,
      loginBase: cfg.loginBase,
      registerBase: cfg.registerBase,
      returnPath: cfg.returnPath,
      stateCollectorFn: collectStateParams
    });

    /* ---------------------------
       Distances dropdown
  --------------------------- */
    function normalizeDistances(distances) {
      const base = Array.isArray(distances) ? distances.slice() : [];
      // Always add a "Custom" option at bottom.
      base.push({ key: "custom", name: "Custom", km: null, mi: null, tag: "custom" });
      return base;
    }

    const distances = normalizeDistances(cfg.distances);

    function fillDistanceSelect() {
      if (!goalDistance) return;
      goalDistance.innerHTML = "";
      distances.forEach(d => {
        const opt = document.createElement("option");
        opt.value = String(d.key);
        opt.textContent = d.name || "Distance";
        goalDistance.appendChild(opt);
      });
      // default first item unless config says otherwise
      if (!goalDistance.value) goalDistance.value = String(distances[0].key);
    }

    function selectedDistanceValue() {
      if (!goalDistance) return { km: NaN, mi: NaN };
      const key = goalDistance.value;
      if (key === "custom") {
        const raw = safeNumber(customDistance && customDistance.value, NaN);
        if (!Number.isFinite(raw) || raw <= 0) return { km: NaN, mi: NaN };
        return isImperial() ? { mi: raw, km: miToKm(raw) } : { km: raw, mi: kmToMi(raw) };
      }
      const d = distances.find(x => String(x.key) === String(key));
      if (!d) return { km: NaN, mi: NaN };
      const km = safeNumber(d.km, NaN);
      const mi = safeNumber(d.mi, NaN);
      // allow one side missing
      if (isImperial()) {
        if (Number.isFinite(mi)) return { mi, km: miToKm(mi) };
        if (Number.isFinite(km)) return { km, mi: kmToMi(km) };
      } else {
        if (Number.isFinite(km)) return { km, mi: kmToMi(km) };
        if (Number.isFinite(mi)) return { mi, km: miToKm(mi) };
      }
      return { km: NaN, mi: NaN };
    }

    function refreshCustomVisibility() {
      const isCustom = goalDistance && goalDistance.value === "custom";
      customOnly.forEach(el => el.classList.toggle("is-hidden", !isCustom));
    }

    /* ---------------------------
       Calculations
  --------------------------- */
    function paceSecPerKmFromInputs() {
      const p = secondsFromMS(paceMin && paceMin.value, paceSec && paceSec.value);
      if (!Number.isFinite(p) || p <= 0) return NaN;
      // If user is in imperial, their pace is per mile
      if (isImperial()) {
        // convert seconds per mile -> seconds per km
        return p / 1.609344;
      }
      return p;
    }

    function secondsPerKmToPerMile(secPerKm) {
      if (!Number.isFinite(secPerKm) || secPerKm <= 0) return NaN;
      return secPerKm * 1.609344;
    }

    function totalTimeSecondsFromGoal() {
      const t = secondsFromHMS(goalH && goalH.value, goalM && goalM.value, goalS && goalS.value);
      return Number.isFinite(t) && t > 0 ? t : NaN;
    }

    function requiredPaceSecPerKm(distanceKm, timeSec) {
      if (!Number.isFinite(distanceKm) || distanceKm <= 0) return NaN;
      if (!Number.isFinite(timeSec) || timeSec <= 0) return NaN;
      return timeSec / distanceKm;
    }

    function showRequiredPace(secPerKm) {
      if (!paceRequiredCard) return;
      if (!Number.isFinite(secPerKm) || secPerKm <= 0) {
        paceRequiredCard.classList.add("is-hidden");
        return;
      }
      paceRequiredCard.classList.remove("is-hidden");
      const perMi = secondsPerKmToPerMile(secPerKm);

      if (reqPerKm) reqPerKm.textContent = `${formatSecondsToPace(secPerKm)} /km`;
      if (reqPerMi) reqPerMi.textContent = `${formatSecondsToPace(perMi)} /mi`;
    }

    function clearSplits() {
      if (splitsBody) splitsBody.innerHTML = "";
      if (splitsWrap) splitsWrap.classList.add("is-hidden");
    }

    function renderSplits(distanceKm, secPerKm) {
      if (!cfg.defaults.showSplits) return;
      if (!splitsWrap || !splitsBody) return;
      if (!Number.isFinite(distanceKm) || distanceKm <= 0) return;
      if (!Number.isFinite(secPerKm) || secPerKm <= 0) return;

      const stepVal = splitStep ? splitStep.value : "km_1";
      let stepKm = 1;
      let label = "km";

      if (stepVal === "km_0.5") { stepKm = 0.5; label = "km"; }
      if (stepVal === "km_1")   { stepKm = 1;   label = "km"; }
      if (stepVal === "mi_1")   { stepKm = 1.609344; label = "mi"; }

      const rows = [];
      for (let d = stepKm; d < distanceKm + 1e-9; d += stepKm) {
        const t = d * secPerKm;
        rows.push({ mark: d, time: t });
      }
      // Ensure final row equals full distance
      if (rows.length === 0 || Math.abs(rows[rows.length - 1].mark - distanceKm) > 1e-6) {
        rows.push({ mark: distanceKm, time: distanceKm * secPerKm });
      }

      splitsBody.innerHTML = "";
      rows.forEach(r => {
        const tr = document.createElement("tr");
        const td1 = document.createElement("td");
        const td2 = document.createElement("td");

        if (label === "mi") td1.textContent = `${(r.mark / 1.609344).toFixed(1).replace(/\.0$/, "")} mi`;
        else td1.textContent = `${r.mark.toFixed(stepKm < 1 ? 1 : 0).replace(/\.0$/, "")} km`;

        td2.textContent = formatSecondsToHMS(r.time);
        tr.appendChild(td1);
        tr.appendChild(td2);
        splitsBody.appendChild(tr);
      });

      splitsWrap.classList.remove("is-hidden");
    }

    function renderCardsFromPace(secPerKm) {
      if (!cardsGrid) return;
      cardsGrid.innerHTML = "";

      if (!Number.isFinite(secPerKm) || secPerKm <= 0) return;

      // show finish times for standard distances from cfg.distances (excluding custom)
      const list = distances.filter(d => String(d.key) !== "custom");
      list.forEach(d => {
        const km = safeNumber(d.km, NaN);
        const kmDist = Number.isFinite(km) ? km : (Number.isFinite(d.mi) ? miToKm(d.mi) : NaN);
        if (!Number.isFinite(kmDist) || kmDist <= 0) return;

        const timeSec = kmDist * secPerKm;

        const card = document.createElement("div");
        card.className = "distance-card";

        const name = document.createElement("div");
        name.className = "name";
        name.textContent = d.name || "Distance";

        const sub = document.createElement("div");
        sub.className = "sub";
        sub.textContent = d.tag ? String(d.tag) : "";

        const time = document.createElement("div");
        time.className = "time";
        time.textContent = formatSecondsToHMS(timeSec);

        card.appendChild(name);
        if (d.tag) card.appendChild(sub);
        card.appendChild(time);
        cardsGrid.appendChild(card);
      });
    }

    function calculateFromPace() {
      const secPerKm = paceSecPerKmFromInputs();
      showRequiredPace(NaN); // hide in pace mode
      clearSplits();
      renderCardsFromPace(secPerKm);
    }

    function calculateFromGoal() {
      const dist = selectedDistanceValue();
      const timeSec = totalTimeSecondsFromGoal();

      if (!Number.isFinite(dist.km) || !Number.isFinite(timeSec)) {
        showRequiredPace(NaN);
        clearSplits();
        return;
      }
      const secPerKm = requiredPaceSecPerKm(dist.km, timeSec);
      showRequiredPace(secPerKm);
      if (cfg.defaults.showSplits) {
        clearSplits();
        renderSplits(dist.km, secPerKm);
      }
    }

    /* ---------------------------
       Events
  --------------------------- */
    unitToggle && unitToggle.addEventListener("change", () => {
      updateUnitLabels();
      refreshCustomVisibility();
      if (!cfg.isLoggedIn) return;
      if (activeMode() === "pace") calculateFromPace();
      else calculateFromGoal();
    });

    modeBtns.forEach(btn => {
      btn.addEventListener("click", () => {
        setMode(btn.dataset.mode);
        if (!cfg.isLoggedIn) return;
        if (activeMode() === "pace") calculateFromPace();
        else calculateFromGoal();
      });
    });

    goalDistance && goalDistance.addEventListener("change", () => {
      refreshCustomVisibility();
      if (!cfg.isLoggedIn) return;
      if (activeMode() === "goal") calculateFromGoal();
    });

    customDistance && customDistance.addEventListener("input", () => {
      if (!cfg.isLoggedIn) return;
      if (activeMode() === "goal") calculateFromGoal();
    });

    [paceMin, paceSec].forEach(el => el && el.addEventListener("change", () => {
      if (!cfg.isLoggedIn) return;
      if (activeMode() === "pace") calculateFromPace();
    }));

    [goalH, goalM, goalS].forEach(el => el && el.addEventListener("change", () => {
      if (!cfg.isLoggedIn) return;
      if (activeMode() === "goal") calculateFromGoal();
    }));

    splitStep && splitStep.addEventListener("change", () => {
      if (!cfg.isLoggedIn) return;
      if (activeMode() === "goal") calculateFromGoal();
    });

    applyPace && applyPace.addEventListener("click", () => {
      if (!cfg.isLoggedIn) { modal.show(); return; }
      calculateFromPace();
    });

    applyGoal && applyGoal.addEventListener("click", () => {
      if (!cfg.isLoggedIn) { modal.show(); return; }
      calculateFromGoal();
    });

    /* ---------------------------
       Init selects (THIS IS WHY DROPDOWNS WERE EMPTY)
  --------------------------- */
    fillSelectRange(paceMin, 0, 30);
    fillSelectRange(paceSec, 0, 59);

    fillSelectRange(goalH, 0, 9);
    fillSelectRange(goalM, 0, 59);
    fillSelectRange(goalS, 0, 59);

    fillDistanceSelect();
    refreshCustomVisibility();

    // Defaults
    if (paceMin) paceMin.value = "5";
    if (paceSec) paceSec.value = "0";
    if (goalH) goalH.value = "0";
    if (goalM) goalM.value = "45";
    if (goalS) goalS.value = "0";

    if (unitToggle) unitToggle.checked = (cfg.defaults.units === "imperial");
    updateUnitLabels();
    setMode(cfg.defaults.mode);

    applyPrefill(parsePrefillFromURL());

    // Initial render (no-gated output)
    clearSplits();
    showRequiredPace(NaN);

    return { calculateFromPace, calculateFromGoal };
  }

  /* ---------------------------
     Global export
  --------------------------- */
  window.RocUpCalcCore = {
    mountTreadmillCalculator,
    mountPaceCalculator,
    formatSecondsToPace,
    formatSecondsToHMS,
    kmToMi,
    miToKm
  };
})();
